<template>
  <Container name="contact">
    <template slot="first">
      <Title title="Kontakt" class="contact-title" />

      <p class="text">
        Twoje zdanie jest dla nas ważne. <br />
        To się nigdy nie zmieni. W końcu warzymy piwo <br />
        właśnie dla Ciebie. Jeżeli chcesz <br />
        się z nami skontaktować, <br />
        wypełnij poniższy formularz.
      </p>
    </template>

    <template slot="second">
      <transition name="fade" mode="out-in">
        <form
          v-if="status === 'form'"
          ref="form"
          key="form"
          class="form"
          novalidate
          @submit.prevent="submit"
        >
          <form-item
            v-model="$v.form.name.$model"
            :errors="errors('name', ['required'])"
            :disabled="loading"
            label="imię i nazwisko"
            placeholder="imię i nazwisko"
            required
            @change="clearError('name')"
          />

          <form-item
            v-model="$v.form.email.$model"
            :errors="errors('email', ['required', 'email'])"
            :disabled="loading"
            type="email"
            label="email"
            placeholder="email"
            required
            @change="clearError('email')"
          />

          <form-item
            v-model="$v.form.message.$model"
            :errors="errors('message', ['required'])"
            :disabled="loading"
            type="textarea"
            label="Wiadomość"
            placeholder="Treść"
            required
            @change="clearError('message')"
          />

          <!-- basic checkbox -->
          <checkbox-component
            ref="basic-checkbox"
            v-model="$v.form.agreement.$model"
            :errors="errors('agreement', ['required'])"
            :loading="loading"
            required
            class="checkbox checkbox--basic"
            :class="{ 'checkbox--visible': !isAgreementExtended }"
            @toggleAgreement="toggleAgreement"
            @change="clearError('agreement')"
          />
          <!-- e/o basic checkbox -->

          <!-- extended checkbox -->
          <checkbox-extended
            ref="extended-checkbox"
            v-model="$v.form.agreement.$model"
            extended
            class="checkbox checkbox--extended"
            :class="{ 'checkbox--visible': isAgreementExtended }"
            @toggleAgreement="toggleAgreement"
            @change="clearError('agreement')"
          />
          <!-- e/o extended checkbox -->

          <div class="button-wrapper">
            <button ref="button" class="button" type="submit" title="Wyślij">
              <img class="button__img img" src="/images/send.svg" alt="" />
            </button>
          </div>

          <div ref="overlay" class="form-overlay" />
        </form>

        <!-- success -->
        <div
          v-else-if="status === 'success'"
          key="success"
          :style="statusWrapperStyle"
          class="status-wrapper status-wrapper--success success"
        >
          <img
            class="img img--success"
            src="/images/success.svg"
            alt="Wiadomość wysłana"
          />
          <span class="success__span span span--success"
            >Wiadomość wysłana</span
          >
        </div>
        <!-- e/o success -->

        <!-- error -->
        <div
          v-else
          key="error"
          :style="statusWrapperStyle"
          class="status-wrapper status-wrapper--error"
        >
          <p class="text">
            Wystąpił nieoczekiwany błąd. <br />
            Prosimy spróbować ponownie.
          </p>
        </div>
        <!-- e/o error -->
      </transition>
    </template>
  </Container>
</template>

<script>
import Title from '../common/Title'
import Container from './PopupContainer'
import FormItem from '../form-items/FormItem'
import CheckboxComponent from '../form-items/Checkbox'
import CheckboxExtended from '../form-items/CheckboxExtended'

import { validationMixin } from 'vuelidate'
import { required, email, sameAs } from 'vuelidate/lib/validators'

import gsap from 'gsap'

const ERRORS = {
  required: 'To pole jest wymagane',
  email: 'Podaj prawidłowy adres e-mail'
}

export default {
  components: {
    Container,
    Title,
    FormItem,
    CheckboxComponent,
    CheckboxExtended
  },
  mixins: [validationMixin],
  data() {
    return {
      form: {
        name: '',
        email: '',
        message: '',
        agreement: false
      },
      apiErrors: {},
      loading: false,
      status: 'form',
      wrapperHeight: 0,
      isAgreementExtended: false
    }
  },
  computed: {
    errors() {
      return (key, errorTypes) =>
        errorTypes.reduce(
          (acc, type) =>
            !this.$v.form[key][type] && this.$v.form[key].$dirty
              ? [...acc, ERRORS[type]]
              : acc,
          this.apiErrors?.[key] || []
        )
    },
    statusWrapperStyle() {
      return {
        height: `${this.wrapperHeight}px`,
        maxHeight: `${this.wrapperHeight}px`
      }
    }
  },
  validations: {
    form: {
      name: { required },
      email: { required, email },
      message: { required },
      agreement: { required: sameAs(() => true) }
    }
  },
  watch: {
    '$store.state.activePopup'(value) {
      if (value === 'contact') {
        this.status = 'form'
        this.$v.$reset()
      }
    }
  },
  mounted() {
    const { form } = this.$refs
    this.wrapperHeight = form.clientHeight
  },
  methods: {
    submit() {
      if (this.loading) return
      this.$v.form.$touch()

      if (!this.$v.form.$invalid) {
        this.loading = true

        this.$axios
          .post('/contact/', this.form)
          .then(() => {
            this.status = 'success'
          })
          .catch(error => {
            if (error.response?.status === 400) {
              // if not validate
              this.apiErrors = error.response.data
            } else {
              // different error
              this.status = 'error'
            }
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    clearError(name) {
      if (this.apiErrors[name]) this.apiErrors[name] = ''
    },
    toggleAgreement(value) {
      this.isAgreementExtended = value

      const {
        button,
        overlay: formOverlay,
        'basic-checkbox': basicCheckbox,
        'extended-checkbox': extendedCheckbox
      } = this.$refs

      const basicEl = basicCheckbox.$el
      const checkboxOverlay = extendedCheckbox.$refs.overlay

      const heightToMove = -basicEl.offsetTop

      const tl = gsap.timeline()

      if (value) {
        // extend agreement
        tl.to(button, { y: '100', duration: 0.3, ease: 'power1.easeInOut' })
          .addLabel('moveUp')
          .to(
            basicEl,
            { y: `${heightToMove}`, duration: 0.5, ease: 'power1.easeInOut' },
            'moveUp'
          )
          .to(
            formOverlay,
            { scaleY: 1, duration: 0.5, ease: 'power1.easeInOut' },
            'moveUp'
          )
          .to(
            checkboxOverlay,
            { scaleY: 1, duration: 0.5, ease: 'power1.easeInOut' },
            'moveUp'
          )
          .to(
            basicEl,
            { opacity: 0, duration: 0.1, delay: 0.5, ease: 'power1.easeInOut' },
            'moveUp'
          )
          .to(
            extendedCheckbox.$el,
            { opacity: 1, duration: 0.1, delay: 0.4, ease: 'power1.easeInOut' },
            'moveUp'
          )
          .to(checkboxOverlay, {
            scaleY: 0,
            duration: 0.5,
            ease: 'power1.easeInOut'
          })
          .set(formOverlay, { opacity: 0 })
      } else {
        // hide agreement
        tl.addLabel('roll')
          .to(checkboxOverlay, {
            scaleY: 1,
            duration: 0.5,
            ease: 'power1.easeInOut'
          })
          .to(
            basicEl,
            { opacity: 1, duration: 0.1, delay: 0.4, ease: 'power1.easeInOut' },
            'roll'
          )
          .to(
            extendedCheckbox.$el,
            { opacity: 0, duration: 0.1, delay: 0.5, ease: 'power1.easeInOut' },
            'roll'
          )
          .to(
            formOverlay,
            { opacity: 1, duration: 0.1, delay: 0.4, ease: 'power1.easeInOut' },
            'roll'
          )
          .addLabel('moveDown')
          .to(
            checkboxOverlay,
            { scaleY: 0, duration: 0.5, ease: 'power1.easeInOut' },
            'moveDown'
          )
          .to(
            formOverlay,
            { scaleY: 0, duration: 0.5, ease: 'power1.easeInOut' },
            'moveDown'
          )
          .to(
            basicEl,
            { y: 0, duration: 0.5, ease: 'power1.easeInOut' },
            'moveDown'
          )
          .to(button, { y: 0, duration: 0.3, ease: 'power1.easeInOut' })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.checkbox {
  pointer-events: none;

  &--visible {
    pointer-events: auto;
  }
}

.contact-title {
  margin-bottom: 34px;

  @include media-down(xl) {
    margin-bottom: 24px;
  }

  @include media-down(lg) {
    font-size: 64px;
    margin-bottom: 15px;
  }

  @include media-down(md) {
    font-size: 30px;
    margin-bottom: 8px;
  }
}

.text {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1.4em;
  font-size: 18px;

  @include media-down(xl) {
    font-size: 12px;
  }
  @include media-down(lg) {
    font-size: 20px;
    margin-bottom: 55px;
  }
  @include media-down(md) {
    font-size: 10px;
    margin-bottom: 25px;
  }
}

.form {
  position: relative;
  max-width: 500px;

  @include media-down(xl) {
    max-width: 420px;
  }

  @include media-down(lg) {
    max-width: unset;
    position: unset;
  }
}

.form-overlay {
  @include fill-space;

  transform-origin: center bottom;
  transform: scaleY(0);
  background-color: $background-color;
  z-index: z(checkbox-overlay);
}

.button-wrapper {
  overflow: hidden;
}

.button {
  @include reset-button;
  display: block;
  margin-left: auto;

  margin-top: 13px;
  width: 42px;

  @include media-down(xl) {
    width: 30px;
  }
  @include media-down(lg) {
    width: 60px;
    margin-top: 30px;
  }
  @include media-down(md) {
    width: 30px;
  }

  transition: opacity 0.2s;

  &:hover,
  &:focus {
    opacity: 0.7;
  }
}

.status-wrapper {
  @include center-content;
}

.img {
  width: 100%;

  &--success {
    max-width: 160px;
  }
}

.success {
  display: flex;
  flex-direction: column;

  &__span {
    font-size: 12px;
    font-family: $bold-font;
    text-transform: uppercase;
    text-align: center;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
